import React from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';

import Button from 'reactstrap/lib/Button';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { hideFinisher } from '@ttstr/actions';
import { useShallowEqualSelector, useActions } from '@ttstr/utils';
import { AppState } from '@ttstr/reducers';

import ContentrArea from '../Contentr/ContentrArea';
import PayPalCheckout from '../Checkout/PayPalCheckout';
import CartOptions from './CartOptions';
import CartListing from './CartListing';

interface OwnProps {
  visible?: boolean;
  closeIcon?: React.ReactNode;
}

type Props = Readonly<OwnProps>;

const ShoppingFinisherModal: React.FC<Props> = ({ closeIcon, visible = true }) => {
  const { t } = useTranslation();
  const isInCheckout = useRouteMatch('/checkout');
  const { showFinisher, noCheckoutMessage } = useShallowEqualSelector(mapStateToProps);
  const { hideFinisher } = useActions(mapDispatchToProps);
  const close = closeIcon ? (
    <button className="close" onClick={hideFinisher}>
      {closeIcon}
    </button>
  ) : null;

  React.useEffect(() => {
    if (showFinisher && isInCheckout) hideFinisher();
  }, [isInCheckout, showFinisher]);

  return (
    <Modal
      className="shopping-finisher-modal"
      isOpen={visible && showFinisher && !isInCheckout}
      size="lg"
      toggle={hideFinisher}
      centered
      autoFocus={false}
    >
      <ModalHeader tag="h2" toggle={hideFinisher} close={close}>
        <span className="h5">{t('CART.SHOPPING_FINISHER_HEADER')}</span>
      </ModalHeader>
      <ModalBody>
        {t('CART.SHOPPING_FINISHER_BODY')}
        <ContentrArea id="below-modal-body my-3" />
        <CartOptions />
        <CartListing className="mt-3" />
      </ModalBody>

      {!noCheckoutMessage ? (
        // just buttons
        <ModalFooter className="sticky-bottom when-valid is-valid d-block pb-1">
          <Row className="align-items-top">
            <Col lg="4" className="p-0 px-1 pb-2">
              <Button
                size="lg"
                block
                color="secondary"
                onClick={hideFinisher}
                className="text-nowrap"
                style={{ height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                autoFocus
              >
                {t(`CART.CONTINUE_SHOPPING`)}
              </Button>
            </Col>
            <Col lg="4" className="p-0 px-1 pt-2 pt-lg-0 mb-n2">
              <PayPalCheckout />
            </Col>
            <Col lg="4" className="p-0 px-1">
              <Button
                tag={Link}
                to="/checkout"
                size="lg"
                block
                color="primary"
                onClick={(e) => {
                  hideFinisher(e);
                }}
                className="text-nowrap"
                style={{ height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {t(`CART.GOTO_CHECKOUT`)}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      ) : (
        // show message and disable checkout button
        <ModalFooter className="sticky-bottom when-valid is-valid d-block">
          <Row className="mb-3">
            <Col>
              <span id="no-checkout-message" className="text-danger">
                {noCheckoutMessage}
              </span>
            </Col>
          </Row>
          <Row className="align-items-center m-0">
            <Col sm="6" className="py-0 px-1">
              <Button size="lg" block color="secondary" className="m-1" onClick={hideFinisher} autoFocus>
                {t(`CART.CONTINUE_SHOPPING`)}
              </Button>
            </Col>
            <Col sm="6" className="py-0 px-1">
              <Button tag={Link} to="#" disabled color="primary" block size="lg" className="m-1">
                {t(`CART.GOTO_CHECKOUT`)}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      )}
    </Modal>
  );
};

function mapStateToProps(state: AppState) {
  const { showFinisher, cart } = state.Cart;
  const noCheckoutMessage = cart?.no_checkout_message;

  return {
    showFinisher,
    noCheckoutMessage,
  };
}

const mapDispatchToProps = {
  hideFinisher,
};

export default React.memo(ShoppingFinisherModal);
